import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Default } from "../../components/default/Default";
import { McaModal, McaDrawer } from "../../components/mca";
import { showToaster, numberFormatter, formatter } from "../../common/common";
import PostRequest from "../../services/PostRequest";
import {
  State,
  getCountryOptions,
  getPostcodeHint,
  countryHasNoPostcode,
} from "../../common/locationData";
import {
  planPayments,
  setUserSubscription,
} from "../../reducer/payment/PlanPayment.action";
import PaypalSubscriptionPayment from "../../components/plan-payment/PaypalSubscriptionPayment";
import moment from "moment";
import axios from "axios";
import { API_URL } from "../../config/Api_url";
import "./plan-payment.css";

const ChangePlanModal = ({ open, onClose, credit, plans, userSubscription }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isChecked, setIsChecked] = useState(false);

  // Determine if user has an active paid subscription
  const hasActivePaidSub =
    !!userSubscription?.paypal_subscription_id &&
    userSubscription?.status === "active";

  // Get current plan quota
  const currentQuota =
    userSubscription?.plan?.scrape_quota ??
    userSubscription?.plan?.credits ??
    0;

  const targetQuota = selectedPlan?.scrape_quota ?? selectedPlan?.credits ?? 0;
  const isUpgrade = targetQuota > currentQuota;
  const changed = selectedPlan && userSubscription?.plan?.id !== selectedPlan?.id;

  useEffect(() => {
    // Preselect current plan
    const current = plans.find((item) => item.id == userSubscription?.plan?.id);
    const fallback = plans.find(
      (p) => !p.is_contact_sales && !p.is_default && p.price_cents !== 0
    );
    setSelectedPlan(current || fallback || null);
    setIsChecked(false);
  }, [open, plans, userSubscription]);

  const handlePlanChange = (e) => {
    const plan = plans.find((item) => item.id == e.target.value);
    setSelectedPlan(plan);
  };

  const formatPlanLabel = (plan) => {
    const quota = numberFormatter.format(
      plan.scrape_quota ?? plan.credits ?? 0
    );
    let priceLabel;
    if (plan.is_contact_sales) priceLabel = "Contact Sales";
    else if ((plan.price_cents ?? 0) === 0) priceLabel = "Free";
    else priceLabel = `${formatter.format((plan.price_cents ?? 0) / 100)}/mo`;
    return `${plan.name} — ${priceLabel} — ${quota} scrapes/mo`;
  };

  const handleServerChange = async () => {
    try {
      const res = await PostRequest("/plans/change/", {
        plan: selectedPlan.code || selectedPlan.id,
      });
      if (res?.approve_url) {
        window.location.href = res.approve_url;
        return;
      }
      showToaster(
        "Downgrade scheduled — it takes effect at your next billing date."
      );
      onClose();
      window.location.reload();
    } catch (error) {
      showToaster(error.message, "error");
    }
  };

  const onSubscriptionComplete = () => {
    onClose();
    window.location.reload();
  };

  let alertClass = "mca-alert";
  let alertBody = "This is your current plan. Pick a different plan to upgrade or downgrade.";
  let actionLabel = "Confirm change";

  if (changed && isUpgrade) {
    alertClass = "mca-alert mca-alert--success";
    alertBody = `Upgrade — charged today (${formatter.format(
      (selectedPlan?.price_cents ?? 0) / 100
    )}). New credits are added on top of your current ${numberFormatter.format(
      (credit?.available_credits ?? 0) - (credit?.used_credits ?? 0)
    )}-credit balance, effective immediately.`;
    actionLabel = "Upgrade now";
  } else if (changed && !hasActivePaidSub) {
    alertClass = "mca-alert mca-alert--success";
    alertBody = `Subscribe to a plan below — your quota is granted once payment is confirmed.`;
    actionLabel = "Subscribe now";
  } else if (changed && !isUpgrade && selectedPlan?.price_cents === 0) {
    alertClass = "mca-alert mca-alert--warning";
    alertBody = `Downgrade to Free. Takes effect at your next billing period (${moment(
      userSubscription?.period_end
    ).format("MMM DD, YYYY")}). No charge today — you keep all ${numberFormatter.format(
      (credit?.available_credits ?? 0) - (credit?.used_credits ?? 0)
    )} credits.`;
    actionLabel = "Schedule downgrade";
  } else if (changed && !isUpgrade) {
    alertClass = "mca-alert mca-alert--warning";
    alertBody = `Downgrade scheduled. Takes effect ${
      moment(
        userSubscription?.period_end
      ).format(
        "MMM DD, YYYY"
      )
    } (${formatter.format(
      (selectedPlan?.price_cents ?? 0) / 100
    )}). No charge today — your ${numberFormatter.format(
      (credit?.available_credits ?? 0) - (credit?.used_credits ?? 0)
    )} credits carry over.`;
    actionLabel = "Schedule downgrade";
  }

  return (
    <McaModal
      open={open}
      onClose={onClose}
      title="Change your plan"
      static
      ariaLabelledBy="cpTitle"
      footer={
        changed && isChecked && (isUpgrade || !hasActivePaidSub) ? (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                color: "var(--mca-muted)",
                textAlign: "center",
              }}
            >
              Complete your {isUpgrade ? "upgrade" : "subscription"} securely
              with PayPal.
            </p>
            <div style={{ width: "100%" }}>
              <PaypalSubscriptionPayment
                plan={selectedPlan}
                onApprove={onSubscriptionComplete}
              />
            </div>
            <button
              className="mca-btn mca-btn--outline"
              onClick={onClose}
              type="button"
              style={{ width: "100%" }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <>
            <button
              className="mca-btn mca-btn--outline"
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            {changed && isChecked && !isUpgrade && hasActivePaidSub ? (
              <button
                className="mca-btn mca-btn--primary"
                onClick={handleServerChange}
                type="button"
              >
                {actionLabel}
              </button>
            ) : null}
          </>
        )
      }
    >
      <div style={{ marginBottom: "16px" }}>
        <p style={{ marginBottom: "6px", fontSize: "14px" }}>
          You have
          <strong>
            {numberFormatter.format(
              (credit?.available_credits ?? 0) - (credit?.used_credits ?? 0)
            )}
            credits
          </strong>
          remaining. They carry over to your new plan.
        </p>
      </div>

      <div className="mca-field" style={{ marginBottom: "18px" }}>
        <label className="mca-label" htmlFor="planSelect">
          Select a plan
        </label>
        <select
          className="mca-select"
          id="planSelect"
          value={selectedPlan?.id || ""}
          onChange={handlePlanChange}
        >
          {plans.map((plan) => (
            <option key={plan.id} value={plan.id}>
              {formatPlanLabel(plan)}
              {userSubscription?.plan?.id === plan.id ? " (current)" : ""}
            </option>
          ))}
        </select>
      </div>

      <div className={alertClass}>
        <svg
          className="lead"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 11v5" />
          <path d="M12 8h.01" />
        </svg>
        <div className="mca-alert__body">{alertBody}</div>
      </div>

      <label className="mca-check" style={{ marginTop: "18px" }}>
        <input
          type="checkbox"
          checked={isChecked}
          onChange={(e) => setIsChecked(e.target.checked)}
        />
        <span className="mca-check__box">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </span>
        <span>I understand the terms above and want to proceed.</span>
      </label>
    </McaModal>
  );
};

const CancelSubscriptionModal = ({
  open,
  onClose,
  credit,
  userSubscription,
}) => {
  const dispatch = useDispatch();
  const [isChecked, setIsChecked] = useState(false);

  const handleCancel = async (e) => {
    e.preventDefault();
    try {
      const options = {
        showLoader: "cancel-sub",
      };
      await PostRequest("/plans/subscription/cancel/", {}, options);
      showToaster("Subscription cancelled successfully.");
      dispatch(setUserSubscription());
      setIsChecked(false);
      onClose(true);
    } catch (error) {
      showToaster(error.message, "error");
    }
  };

  useEffect(() => {
    if (!open) setIsChecked(false);
  }, [open]);

  return (
    <McaModal
      open={open}
      onClose={onClose}
      static
      footer={
        <>
          <button
            className="mca-btn mca-btn--outline"
            onClick={onClose}
            type="button"
          >
            Keep subscription
          </button>
          <button
            className="mca-btn mca-btn--danger"
            id="csAction"
            disabled={!isChecked}
            onClick={handleCancel}
            type="button"
          >
            Yes, cancel my subscription
          </button>
        </>
      }
    >
      <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
        <span
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "9px",
            background: "var(--mca-danger-subtle)",
            color: "var(--mca-danger)",
            display: "grid",
            placeItems: "center",
            flex: "none",
          }}
        >
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10.3 3.8 2.4 17.5A2 2 0 0 0 4.1 20.5h15.8a2 2 0 0 0 1.7-3L13.7 3.8a2 2 0 0 0-3.4 0Z" />
            <path d="M12 9v4.5" />
            <path d="M12 17h.01" />
          </svg>
        </span>
        <div>
          <h3>Cancel subscription</h3>
          <p>You'll keep Pro access until your period ends.</p>
        </div>
      </div>

      <ul
        style={{
          margin: "16px 0",
          paddingLeft: "20px",
          fontSize: "14px",
          lineHeight: "1.9",
          color: "var(--mca-text)",
        }}
      >
        <li>
          Your subscription will not renew after
          <strong>
            {moment(userSubscription?.period_end).format("MMM DD, YYYY")}
          </strong>
          .
        </li>
        <li>Access and credits remain available until that date.</li>
        <li>
          You'll revert to the <strong>Free</strong> plan afterward.
        </li>
        <li>No refunds are issued for unused credits.</li>
      </ul>

      <div className="mca-alert mca-alert--warning" style={{ marginBottom: "16px" }}>
        <svg
          className="lead"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
        <div className="mca-alert__body">
          You currently have
          <strong>
            {numberFormatter.format(
              (credit?.available_credits ?? 0) - (credit?.used_credits ?? 0)
            )}
          </strong>
          credits available until cancellation.
        </div>
      </div>

      <label className="mca-check">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={(e) => setIsChecked(e.target.checked)}
        />
        <span className="mca-check__box">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </span>
        <span>
          I understand my subscription will not renew and unused credits are
          non-refundable.
        </span>
      </label>
    </McaModal>
  );
};


// Shared: download an invoice PDF (auth'd blob), toggling a per-row spinner.
const downloadInvoicePDF = async (invoice, setDownloadingId) => {
  try {
    setDownloadingId(invoice.id);
    const res = await axios.get(
      `${API_URL}/users/payments/${invoice.id}/invoice.pdf`,
      {
        headers: { Authorization: `Token ${localStorage.getItem("TOKEN")}` },
        responseType: "blob",
      }
    );
    const ref = `INV-${String(invoice.id).padStart(6, "0")}`;
    const url = window.URL.createObjectURL(
      new Blob([res.data], { type: "application/pdf" })
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `Invoice-${ref}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.log(error);
    showToaster("Could not download the invoice. Please try again.", "error");
  }
  finally {
    setDownloadingId(null);
  }
};

const invoiceStatusClass = (status) =>
  status === "refunded" || status === "partially_refunded"
    ? "mca-badge--soft-warning"
    : status === "failed"
    ? "mca-badge--soft-danger"
    : "mca-badge--soft-success";

const PdfIcon = ({ width = "1.75" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={width}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3v12" />
    <path d="m8 11 4 4 4-4" />
    <path d="M4 19h16" />
  </svg>
);

// One invoice row (design `.invoice`). `compact` = the icon-only PDF button used
// in the inline main-view list; otherwise the labelled button used in the drawer.
const InvoiceRow = ({ invoice, downloadingId, onDownload, compact = false }) => {
  const amount = ((invoice?.amount_cents || 0) / 100).toFixed(2);
  const invoiceRef = `INV-${String(invoice.id).padStart(6, "0")}`;
  const statusLabel =
    (invoice?.status || "paid").charAt(0).toUpperCase() +
    (invoice?.status || "paid").slice(1);
  const isDownloading = downloadingId === invoice.id;

  return (
    <div className="invoice">
      <div style={{ flex: "1" }}>
        <div className="amt">${amount}</div>
        <div className="dt">
          {moment(invoice?.created_at).format("MMM DD, YYYY")} · {invoiceRef}
        </div>
      </div>
      <span className={`mca-badge ${invoiceStatusClass(invoice?.status)}`}>
        {statusLabel}
      </span>
      {compact ? (
        <button
          className="mca-iconbtn"
          style={{ width: "34px", height: "34px" }}
          aria-label="Download PDF"
          onClick={() => onDownload(invoice)}
          disabled={isDownloading}
          type="button"
        >
          {isDownloading ? <span className="mca-spinner" /> : <PdfIcon />}
        </button>
      ) : (
        <button
          className="mca-btn mca-btn--outline mca-btn--sm"
          onClick={() => onDownload(invoice)}
          disabled={isDownloading}
          type="button"
        >
          <PdfIcon width="2" />
          {isDownloading ? "..." : "PDF"}
        </button>
      )}
    </div>
  );
};

const InvoicesDrawer = ({ open, onClose }) => {
  const [invoicesList, setInvoicesList] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);

  const loadInvoices = async () => {
    try {
      const response = await PostRequest("/users/payments/list/");
      setInvoicesList(response);
    } catch (error) {
      console.log(error.message);
      showToaster("Could not load invoices. Please try again.", "error");
    }
  };

  useEffect(() => {
    if (open) loadInvoices();
  }, [open]);

  return (
    <McaDrawer
      open={open}
      onClose={onClose}
      title="Invoices"
      subtitle="All payments on this account"
    >
      <div>
        {invoicesList && invoicesList.length > 0 ? (
          invoicesList.map((invoice, index) => (
            <InvoiceRow
              key={invoice.id || index}
              invoice={invoice}
              downloadingId={downloadingId}
              onDownload={(inv) => downloadInvoicePDF(inv, setDownloadingId)}
            />
          ))
        ) : (
          <div style={{ textAlign: "center", padding: "24px" }}>
            No invoices found.
          </div>
        )}
      </div>
    </McaDrawer>
  );
};

const BillingAddressDrawer = ({
  open,
  onClose,
  getBillingAddress,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    business_name: "",
    address_1: "",
    address_2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
  });

  const [isLoading, setIsLoading] = useState(false);

  const countryOptions = useMemo(() => getCountryOptions(), []);
  const billingStates = useMemo(
    () => State.getStatesOfCountry(formData.country) || [],
    [formData.country]
  );

  useEffect(() => {
    if (getBillingAddress) {
      setFormData({
        business_name: getBillingAddress.business_name || "",
        address_1: getBillingAddress.address_1 || "",
        address_2: getBillingAddress.address_2 || "",
        city: getBillingAddress.city || "",
        state: getBillingAddress.state || "",
        postal_code: getBillingAddress.postal_code || "",
        country: getBillingAddress.country || "US",
      });
    }
  }, [getBillingAddress, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const formDataObj = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataObj.append(key, value);
      });
      await PostRequest("/users/billing-address/add-or-update/", formDataObj);
      showToaster("Billing address saved.");
      if (onSave) onSave();
      onClose();
    } catch (error) {
      showToaster(error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <McaDrawer
      open={open}
      onClose={onClose}
      title="Billing address"
      subtitle="Used on your invoices"
      footer={
        <>
          <button
            className="mca-btn mca-btn--outline"
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className="mca-btn mca-btn--primary"
            onClick={handleSubmit}
            disabled={isLoading}
            type="button"
          >
            {isLoading ? "Saving..." : "Save address"}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: "16px" }}>
        <div className="mca-field">
          <label className="mca-label">
            Business name <span className="req">*</span>
          </label>
          <input
            className="mca-input"
            type="text"
            name="business_name"
            value={formData.business_name}
            onChange={handleChange}
            required
          />
        </div>

        <div className="mca-field">
          <label className="mca-label">
            Address line 1 <span className="req">*</span>
          </label>
          <input
            className="mca-input"
            type="text"
            name="address_1"
            value={formData.address_1}
            onChange={handleChange}
            required
          />
        </div>

        <div className="mca-field">
          <label className="mca-label">Address line 2</label>
          <input
            className="mca-input"
            type="text"
            name="address_2"
            placeholder="Suite, unit, etc. (optional)"
            value={formData.address_2}
            onChange={handleChange}
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <div className="mca-field">
            <label className="mca-label">
              City <span className="req">*</span>
            </label>
            <input
              className="mca-input"
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mca-field">
            <label className="mca-label">
              State <span className="req">*</span>
            </label>
            {billingStates.length > 0 ? (
              <select
                className="mca-select"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
              >
                <option value="">Select state…</option>
                {billingStates.map((s) => (
                  <option key={s.isoCode} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className="mca-input"
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
              />
            )}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <div className="mca-field">
            <label className="mca-label">
              Postal code <span className="req">*</span>
            </label>
            <input
              className="mca-input"
              type="text"
              name="postal_code"
              placeholder={getPostcodeHint(formData.country).example || "Postal / ZIP code"}
              value={formData.postal_code}
              onChange={handleChange}
              required={!countryHasNoPostcode(formData.country)}
            />
            {getPostcodeHint(formData.country).format && (
              <span className="mca-help">
                Format: {getPostcodeHint(formData.country).format}
              </span>
            )}
          </div>
          <div className="mca-field">
            <label className="mca-label">
              Country <span className="req">*</span>
            </label>
            <select
              className="mca-select"
              name="country"
              value={formData.country}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, country: e.target.value, state: "" }))
              }
              required
            >
              {countryOptions.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </McaDrawer>
  );
};

export const PlanAndPayment = () => {
  const dispatch = useDispatch();
  const [credit, setCredit] = useState({});
  const [getBillingAddress, setGetBillingAddress] = useState({});

  // Modal/Drawer states
  const [changePlanOpen, setChangePlanOpen] = useState(false);
  const [cancelSubOpen, setCancelSubOpen] = useState(false);
  const [invoicesOpen, setInvoicesOpen] = useState(false);
  const [invoicesList, setInvoicesList] = useState([]);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [billingAddrOpen, setBillingAddrOpen] = useState(false);

  const _planPayment = useSelector(
    (item) => item.planPaymentReducer.planPayment
  );
  const _userSubscription = useSelector(
    (item) => item.planPaymentReducer.userSubscription
  );

  // Load data on mount
  const loadCredits = async () => {
    try {
      const response = await PostRequest("/users/credits/");
      setCredit(response);
    } catch (error) {
      console.log("error", error.message);
    }
  };

  const loadBillingAddress = async () => {
    try {
      const response = await PostRequest("/users/get/billing-address/");
      if (response) {
        setGetBillingAddress(response);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  const loadPlans = () => {
    try {
      dispatch(planPayments());
    } catch (error) {
      console.log(error.message);
    }
  };

  const loadUserSubscription = () => {
    try {
      dispatch(setUserSubscription());
    } catch (error) {
      console.log(error.message);
    }
  };

  const loadInvoices = async () => {
    try {
      const response = await PostRequest("/users/payments/list/");
      setInvoicesList(Array.isArray(response) ? response : []);
    } catch (error) {
      console.log("Could not load invoices:", error.message);
    }
  };

  useEffect(() => {
    loadCredits();
    loadBillingAddress();
    loadPlans();
    loadUserSubscription();
    loadInvoices();
  }, []);

  // Plan display logic
  const hasPaidPlan = !!_userSubscription?.paypal_subscription_id;
  const planName = _userSubscription?.plan?.name || "Active credits";
  const planQuota = hasPaidPlan
    ? _userSubscription?.plan?.scrape_quota ?? _userSubscription?.plan?.credits
    : credit?.available_credits;

  // Credits display
  const usedCredits = Number(credit?.used_credits ?? 0);
  const availableCredits = Number(credit?.available_credits ?? 0);
  const usagePct =
    availableCredits > 0
      ? Math.min(100, Math.round((usedCredits / availableCredits) * 100))
      : 0;


  return (
    <Default>
      <div className="mca-content">
        <div className="mca-page-head">
          <h1>Account & Billing</h1>
          <p>Manage your plan, API key, credits, and payment details.</p>
        </div>

        <div className="acct-grid">
          {/* ============ LEFT: Plan & Usage ============ */}
          <div className="mca-card">
            <div className="plan-hero">
              <div className="plan-hero__row">
                <div>
                  <div className="plan-name">
                    {planName}
                    {_userSubscription?.status === "active" && (
                      <span className="mca-badge mca-badge--soft-success">
                        <span className="dot"></span>Active
                      </span>
                    )}
                  </div>
                  <div className="plan-meta">
                    {hasPaidPlan && _userSubscription?.plan ? (
                      <>
                        {formatter.format(
                          (_userSubscription?.plan?.price_cents ?? 0) / 100
                        )}
                        /month · {numberFormatter.format(planQuota)} requests/month ·
                        renews{" "}
                        {credit?.period_end
                          ? moment(credit.period_end).format("MMM DD, YYYY")
                          : "—"}
                      </>
                    ) : (
                      <>Free plan</>
                    )}
                  </div>
                </div>
                <button
                  className="mca-btn mca-btn--primary mca-btn--sm"
                  onClick={() => setChangePlanOpen(true)}
                  type="button"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1-6.7-3" />
                    <path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 6.7 3" />
                    <path d="M21 3v5h-5" />
                    <path d="M3 21v-5h5" />
                  </svg>
                  Change plan
                </button>
              </div>
            </div>

            <div className="mca-card__body">
              {/* Credits */}
              <div className="kv">
                <div className="kv__label">Credits</div>
                <div>
                  <div className="credit-head">
                    <span style={{ fontSize: "14px" }}>
                      <strong>{numberFormatter.format(usedCredits)}</strong> of{" "}
                      <strong>{numberFormatter.format(availableCredits)}</strong>{" "}
                      used this month
                    </span>
                    <span
                      className="credit-pct"
                      style={{
                        color:
                          usagePct >= 90
                            ? "var(--mca-danger)"
                            : usagePct >= 75
                            ? "var(--mca-warning)"
                            : "var(--mca-success)",
                      }}
                    >
                      {usagePct}%
                    </span>
                  </div>
                  <div className="mca-progress">
                    <div
                      className="mca-progress__bar"
                      style={{
                        width: `${usagePct}%`,
                        background:
                          usagePct >= 90
                            ? "var(--mca-danger)"
                            : usagePct >= 75
                            ? "var(--mca-warning)"
                            : "var(--mca-success)",
                      }}
                    ></div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginTop: "10px",
                    }}
                  >
                    <span style={{ fontSize: "12px", color: "var(--mca-muted)" }}>
                      Next reset:{" "}
                      {credit?.period_end
                        ? moment(credit.period_end).format("MMM DD, YYYY")
                        : "—"}
                    </span>
                    <button
                      className="mca-btn mca-btn--ghost mca-btn--sm"
                      onClick={loadCredits}
                      type="button"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 12a9 9 0 1 1-3-6.7" />
                        <path d="M21 4v5h-5" />
                      </svg>
                      Refresh
                    </button>
                  </div>
                </div>
              </div>

              {/* Subscription Management */}
              <div className="kv" style={{ borderBottom: "none" }}>
                <div className="kv__label">Subscription</div>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <button
                    className="mca-btn mca-btn--outline mca-btn--sm"
                    onClick={() => setChangePlanOpen(true)}
                    type="button"
                  >
                    Upgrade / Downgrade
                  </button>
                  {_userSubscription?.paypal_subscription_id &&
                    _userSubscription?.status === "active" && (
                      <button
                        className="mca-btn mca-btn--ghost mca-btn--sm"
                        style={{ color: "var(--mca-danger)" }}
                        onClick={() => setCancelSubOpen(true)}
                        type="button"
                      >
                        Cancel subscription
                      </button>
                    )}
                </div>
              </div>
            </div>
          </div>

          {/* ============ RIGHT: Payment + Invoices ============ */}
          <div className="right-col">
            {/* Billing Address */}
            <div className="mca-card">
              <div className="mca-card__head">
                <div className="mca-card__title">Billing address</div>
                <button
                  className="mca-btn mca-btn--link mca-btn--sm"
                  onClick={() => setBillingAddrOpen(true)}
                  type="button"
                >
                  Edit
                </button>
              </div>
              <div className="mca-card__body">
                <div className="addr">
                  {getBillingAddress?.business_name && (
                    <>
                      {getBillingAddress.business_name}
                      <br />
                    </>
                  )}
                  {getBillingAddress?.address_1 && (
                    <>
                      {getBillingAddress.address_1}
                      {getBillingAddress?.address_2 ? `,` : ""}
                      <br />
                    </>
                  )}
                  {getBillingAddress?.address_2 && (
                    <>
                      {getBillingAddress.address_2}
                      <br />
                    </>
                  )}
                  {getBillingAddress?.city && (
                    <>
                      {getBillingAddress.city}
                      {getBillingAddress?.state ? " " + getBillingAddress.state : ""}
                    </>
                  )}
                  {getBillingAddress?.postal_code && (
                    <span className="pc"> {getBillingAddress.postal_code}</span>
                  )}
                  {getBillingAddress?.country && (
                    <>
                      <br />
                      {getBillingAddress.country}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Invoices — most recent inline; "View all" opens the full drawer */}
            <div className="mca-card">
              <div className="mca-card__head">
                <div className="mca-card__title">
                  Invoices
                  {invoicesList.length > 0 && (
                    <span
                      className="mca-badge mca-badge--soft-muted"
                      style={{ marginLeft: "8px" }}
                    >
                      {invoicesList.length}
                    </span>
                  )}
                </div>
                {invoicesList.length > 3 && (
                  <button
                    className="mca-btn mca-btn--link mca-btn--sm"
                    onClick={() => setInvoicesOpen(true)}
                    type="button"
                  >
                    View all
                  </button>
                )}
              </div>
              <div
                className="mca-card__body"
                style={{ paddingTop: "4px", paddingBottom: "4px" }}
              >
                {invoicesList.length > 0 ? (
                  invoicesList.slice(0, 3).map((invoice, index) => (
                    <InvoiceRow
                      key={invoice.id || index}
                      invoice={invoice}
                      compact
                      downloadingId={downloadingInvoiceId}
                      onDownload={(inv) =>
                        downloadInvoicePDF(inv, setDownloadingInvoiceId)
                      }
                    />
                  ))
                ) : (
                  <div
                    style={{
                      fontSize: "13px",
                      color: "var(--mca-muted)",
                      padding: "14px 0",
                    }}
                  >
                    No invoices yet. Paid invoices will appear here.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODALS & DRAWERS */}
      <ChangePlanModal
        open={changePlanOpen}
        onClose={() => setChangePlanOpen(false)}
        credit={credit}
        plans={_planPayment}
        userSubscription={_userSubscription}
      />

      <CancelSubscriptionModal
        open={cancelSubOpen}
        onClose={() => setCancelSubOpen(false)}
        credit={credit}
        userSubscription={_userSubscription}
      />


      <InvoicesDrawer
        open={invoicesOpen}
        onClose={() => setInvoicesOpen(false)}
      />

      <BillingAddressDrawer
        open={billingAddrOpen}
        onClose={() => setBillingAddrOpen(false)}
        getBillingAddress={getBillingAddress}
        onSave={loadBillingAddress}
      />
    </Default>
  );
};
